import { useEffect, useState } from "react";
import { Download, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api, errMessage } from "../../lib/api";

// COPPA parent data controls: one-click view/export/delete of each child's
// learning history. Deletion keeps the profile + consent records.
export const ParentDataControls = () => {
  const [kids, setKids] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/children").then(({ data }) => setKids(data)).catch(() => {});
  }, []);

  const exportData = async (child) => {
    setBusy(child.child_id);
    try {
      const { data } = await api.get(`/children/${child.child_id}/data-export`,
        { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${child.display_name}-data-export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${child.display_name}'s data export downloaded`);
    } catch (e) { toast.error(errMessage(e)); }
    setBusy("");
  };

  const deleteHistory = async (child) => {
    if (!window.confirm(
      `Permanently delete ALL of ${child.display_name}'s learning history (practice results, badges, math progress, stories)? The profile itself stays. This cannot be undone.`)) return;
    setBusy(child.child_id);
    try {
      const { data } = await api.delete(`/children/${child.child_id}/learning-history`);
      toast.success(`Deleted ${data.total} learning record${data.total === 1 ? "" : "s"} for ${child.display_name}`);
    } catch (e) { toast.error(errMessage(e)); }
    setBusy("");
  };

  if (!kids.length) return null;
  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="parent-data-controls">
      <div>
        <h3 className="text-sm font-bold font-display flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-secondary" aria-hidden="true" /> Parent data controls
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          You own your child's data. Export everything we hold, or erase their learning
          history in one click. Photos and voice audio are never stored — only scores.
        </p>
      </div>
      <div className="space-y-2">
        {kids.map((k) => (
          <div key={k.child_id} className="flex items-center gap-2 flex-wrap rounded-xl border border-border px-3 py-2"
            data-testid={`data-controls-${k.child_id}`}>
            <p className="text-sm font-bold flex-1 min-w-24">{k.display_name}</p>
            <Button size="sm" variant="outline" className="rounded-full gap-1 text-xs h-8"
              disabled={busy === k.child_id} onClick={() => exportData(k)}
              data-testid={`data-export-${k.child_id}`}>
              <Download className="h-3.5 w-3.5" aria-hidden="true" /> Export data
            </Button>
            <Button size="sm" variant="outline"
              className="rounded-full gap-1 text-xs h-8 text-destructive border-destructive/40 hover:bg-destructive/10"
              disabled={busy === k.child_id} onClick={() => deleteHistory(k)}
              data-testid={`data-delete-${k.child_id}`}>
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete history
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};
